import { codeFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CalculateIcon from '@mui/icons-material/Calculate';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DecimalField from '../shared/DecimalField';

/**
 * Editable discount table (alennustaulukko) + recompute button.
 *
 * Rendered inside the catalogue's tabbed section and only mounted once its tab
 * is opened, so the fetch-on-mount here is effectively lazy. Editing a
 * percentage and hitting "Laske hinnat uudelleen" re-derives all purchase
 * prices of the live batch on the server — no catalogue re-upload.
 */
function DiscountGroupsSection({ catalogue, onRecomputeStarted, onError }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [newGroup, setNewGroup] = useState({ group_code: '', discount_percent: '', name: '' });
  const [recomputing, setRecomputing] = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/catalogue-discount-groups/', {
        params: { catalogue: catalogue.id },
      });
      setGroups(response.data);
    } catch {
      onError(t('catalogues.discountLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [catalogue.id, onError, t]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleSaveEdit = async (group) => {
    const value = edits[group.id];
    if (value === undefined || value === '') return;
    setSavingId(group.id);
    try {
      await axios.patch(`/api/catalogue-discount-groups/${group.id}/`, {
        discount_percent: value,
      });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[group.id];
        return next;
      });
      fetchGroups();
    } catch {
      onError(t('catalogues.discountSaveFailed'));
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (group) => {
    try {
      await axios.delete(`/api/catalogue-discount-groups/${group.id}/`);
      fetchGroups();
    } catch {
      onError(t('catalogues.discountSaveFailed'));
    }
  };

  const handleAdd = async () => {
    if (!newGroup.group_code.trim() || newGroup.discount_percent === '') return;
    try {
      await axios.post('/api/catalogue-discount-groups/', {
        catalogue: catalogue.id,
        group_code: newGroup.group_code.trim(),
        discount_percent: newGroup.discount_percent,
        name: newGroup.name.trim(),
      });
      setNewGroup({ group_code: '', discount_percent: '', name: '' });
      fetchGroups();
    } catch {
      onError(t('catalogues.discountSaveFailed'));
    }
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const response = await axios.post(`/api/vendor-catalogues/${catalogue.id}/recompute-prices/`);
      onRecomputeStarted(response.data);
    } catch (err) {
      onError(err.response?.data?.detail || t('catalogues.recomputeFailed'));
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <Box data-testid="discount-groups-section">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Tooltip title={t('catalogues.recomputeHelp')}>
            <span>
              <Button
                variant="outlined"
                startIcon={recomputing ? <CircularProgress size={16} /> : <CalculateIcon />}
                onClick={handleRecompute}
                disabled={recomputing || !catalogue.current_import}
                data-testid="recompute-prices-button"
              >
                {t('catalogues.recomputePrices')}
              </Button>
            </span>
          </Tooltip>
        </Box>

      {/* Table chrome (incl. headers) renders immediately on tab switch; only
          the body shows a spinner while groups load, so the tab never feels stuck. */}
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('catalogues.groupCode')}</TableCell>
              <TableCell>{t('catalogues.discountPercent')}</TableCell>
              <TableCell>{t('catalogues.groupName')}</TableCell>
              <TableCell width="1%" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow data-testid="discount-groups-loading">
                <TableCell colSpan={4} sx={{ textAlign: 'center', py: 5, border: 0 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && (
              <>
              {groups.map((group) => (
                <TableRow key={group.id} data-testid={`discount-group-${group.group_code}`}>
                  <TableCell sx={{ ...codeFont }}>{group.group_code}</TableCell>
                  <TableCell sx={{ width: 140 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DecimalField
                        value={edits[group.id] !== undefined ? edits[group.id] : group.discount_percent}
                        onChange={(value) => setEdits((prev) => ({ ...prev, [group.id]: value }))}
                        maxDecimals={3}
                        size="small"
                        sx={{ width: 90 }}
                        inputProps={{ 'data-testid': `discount-input-${group.group_code}` }}
                      />
                      {edits[group.id] !== undefined && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleSaveEdit(group)}
                          disabled={savingId === group.id}
                          data-testid={`discount-save-${group.group_code}`}
                        >
                          {savingId === group.id ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{group.name}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleDelete(group)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder={t('catalogues.groupCode')}
                    value={newGroup.group_code}
                    onChange={(e) => setNewGroup({ ...newGroup, group_code: e.target.value })}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell>
                  <DecimalField
                    value={newGroup.discount_percent}
                    onChange={(value) => setNewGroup({ ...newGroup, discount_percent: value })}
                    maxDecimals={3}
                    size="small"
                    sx={{ width: 90 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder={t('catalogues.groupName')}
                    value={newGroup.name}
                    onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" color="primary" onClick={handleAdd}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default DiscountGroupsSection;
